/**
 * TwoFactorSettings - the moderator's two-step verification card
 * ---------------------------------------------------------------
 * Only moderators see this, and only because only moderators have a second
 * step. The server decides that, not this component: it asks
 * GET /auth/me/two-factor and renders nothing at all unless the answer says
 * the account is one.
 *
 * There is deliberately no OFF switch. A moderator can read reported
 * conversations between an adult and a child, and a safeguard that can be
 * switched off on a busy afternoon is not one. This card is for the part a
 * moderator genuinely needs to manage: the recovery codes.
 *
 * WHY THE RECOVERY CODES MATTER MORE HERE THAN USUAL
 * The second factor is an email. So an inbox that is unreachable - a locked
 * phone, a mail outage, a work account that has been suspended - takes
 * moderation down with it, and moderation is what somebody reaching for a
 * report needs. These codes are the only route in that does not touch email.
 */

import { useState, useEffect } from 'react';
import { ShieldCheck, KeyRound, AlertTriangle } from 'lucide-react';

import { authApi } from '../services/api';
import { dateOnly } from '../utils/format';
import { useI18n } from '../hooks/useI18n';
import PasswordInput from './PasswordInput';

export default function TwoFactorSettings() {
  const { t } = useI18n();
  const [status, setStatus] = useState(null);
  const [password, setPassword] = useState('');
  const [codes, setCodes] = useState(null); // shown once, then gone for ever
  const [state, setState] = useState({ busy: false, error: '' });

  useEffect(() => {
    authApi
      .twoFactorStatus()
      .then(setStatus)
      .catch(() => setStatus({ required: false }));
  }, []);

  // Not a moderator, or we could not ask. Either way there is nothing here.
  if (!status?.required) return null;

  const generate = async (event) => {
    event.preventDefault();
    setState({ busy: true, error: '' });

    try {
      const result = await authApi.newRecoveryCodes(password);
      setCodes(result.codes);
      setPassword('');
      // Keep the count on screen honest without a second round trip.
      setStatus((s) => ({
        ...s,
        recoveryCodesRemaining: result.codes.length,
        recoveryCodesIssuedAt: new Date().toISOString(),
      }));
      setState({ busy: false, error: '' });
    } catch (err) {
      setState({ busy: false, error: err.message });
    }
  };

  const remaining = status.recoveryCodesRemaining ?? 0;

  return (
    <div className="card" style={{ marginBottom: 'var(--space-md)' }}>
      <h3 style={{ marginBottom: 'var(--space-sm)' }}>
        <ShieldCheck size={20} aria-hidden="true" /> {t('twoFactor2.title')}
      </h3>

      <p className="text-sm text-muted">
        {t('twoFactor2.whyBefore')} <strong>{t('twoFactor2.and')}</strong>{' '}
        {t('twoFactor2.whyAfter')} <strong>{status.sentTo}</strong>{' '}
        {t('twoFactor2.eachTime')}
      </p>

      {status.lastVerifiedAt && (
        <p className="text-sm text-muted">
          {t('twoFactor2.lastLogin', { date: dateOnly(status.lastVerifiedAt) })}
        </p>
      )}

      <hr className="divider" />

      <h4>{t('twoFactor2.recoveryCodes')}</h4>
      <p className="text-sm text-muted">
        {t('twoFactor2.eachLogsInOnce')} <strong>{t('twoFactor2.not')}</strong>{' '}
        {t('twoFactor2.yourInbox')}
      </p>

      {/* Two left is the point at which this stops being a comfortable margin
          and starts being a problem waiting for a bad day. */}
      {remaining <= 2 ? (
        <div className="alert alert-warning">
          <AlertTriangle size={18} aria-hidden="true" />
          <span>
            {remaining === 0
              ? t('twoFactor2.noneLeft')
              : t('twoFactor2.fewLeft', { count: remaining })}
          </span>
        </div>
      ) : (
        <p className="text-sm">
          {status.recoveryCodesIssuedAt
            ? t('twoFactor2.unusedIssued', {
                count: remaining,
                date: dateOnly(status.recoveryCodesIssuedAt),
              })
            : t('twoFactor2.unusedOnly', { count: remaining })}
        </p>
      )}

      {codes && (
        <>
          <div className="alert alert-success">
            {t('twoFactor2.writeThemDown')}
          </div>
          <div className="recovery-codes">
            {codes.map((c) => (
              <span key={c}>{c}</span>
            ))}
          </div>
        </>
      )}

      <form className="form" onSubmit={generate}>
        {state.error && <div className="alert alert-error">{state.error}</div>}

        <div className="field">
          <label htmlFor="twoFactorPassword">{t('twoFactor2.yourPassword')}</label>
          <PasswordInput
            id="twoFactorPassword"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
          {/* Same reasoning as deleting an account: a borrowed laptop with a
              live session should not be enough to walk away with ten
              permanent ways into a moderator account. */}
          <span className="hint">
            {t('twoFactor2.passwordWhy')}
          </span>
        </div>

        <button
          type="submit"
          className="btn btn-outline btn-inline"
          disabled={state.busy}
          style={{ alignSelf: 'flex-start' }}
        >
          <KeyRound size={18} />
          {state.busy ? t('twoFactor2.generating') : t('twoFactor2.generateNew')}
        </button>
        <span className="hint">{t('twoFactor2.oldStop')}</span>
      </form>
    </div>
  );
}
